import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import moment from 'moment';
import { signOut, fetchAuthSession, signIn, resetPassword, confirmResetPassword, confirmSignIn } from 'aws-amplify/auth';
import Environment from '../environment';
import { CMS_PERMISSIONS } from '../utils/const';

const createAxiosService = (url: string) => {
    const axiosInstance = axios.create({
        baseURL: url,
    });
    axiosInstance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
        // @ts-ignore
        const user: any = window?.user;
        if (user?.AuthenticationResult?.AccessToken) {
            // @ts-ignore
            request.headers['Authorization'] = `Bearer ${user?.AuthenticationResult?.AccessToken}`;
        }
        return request;
    });

    axiosInstance.interceptors.response.use(
        async (response: AxiosResponse) => {
            if (response.status === 401) {
                // Logout
                await signOut();
                window.location.reload();
            }
            return response;
        },
        async (error) => {
            if (error.response) {
                if (error.response?.status === 401) {
                    await signOut();
                    window.location.reload();
                }
                return {
                    status: error.response?.status || 400,
                    data: error.response?.data,
                };
            }

            return Promise.reject(error);
        }
    );
    return axiosInstance;
};

export const coreService = createAxiosService(Environment.coreServiceUrl);
export const appService = createAxiosService(Environment.appServiceUrl);
export const socialService = createAxiosService(Environment.socialServiceUrl);
export const exoseedService = createAxiosService(Environment.exoseedServiceUrl);
// const utilService = createAxiosService('https://53stwiz7gd.execute-api.ap-southeast-1.amazonaws.com');

export const elevationCalc = (points: any) => {
    return appService.post(
        `/trail-map/calc-elevation`,
        {
            coordinates: points,
            planned_elevation: [],
        },
        {
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );
};

export function formatValues(values: any) {
    const result: any = {};
    if (values) {
        for (const valuesKey in values) {
            result[valuesKey] = values[valuesKey];
            if (valuesKey && typeof values[valuesKey] === 'string') {
                result[valuesKey] = (values[valuesKey] || '').toString().trim();
            }
        }
    }
    return result;
}

export const duration = (time: number, unit: any) => {
    const item = moment.duration(time, unit);
    return `${item.hours()}h ${item.minutes()}m `;
};

export const durationActivity = (time: number, unit: any) => {
    const addZero = (num: number) => {
        return num < 10 ? `0${num}` : num;
    };

    const item = moment.duration(time, unit);
    if (item.hours()) {
        return `${addZero(item.hours())}:${addZero(item.minutes())}:${addZero(item.seconds())}`;
    }
    return `${addZero(item.minutes())}:${addZero(item.seconds())}`;
};

export function numberWithSpaces(x: any) {
    var parts = x.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
}

export function round(value: any, exp: number) {
    if (typeof exp === 'undefined' || +exp === 0) return Math.round(value);

    value = +value;
    exp = +exp;

    if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) return NaN;

    // Shift
    value = value.toString().split('e');
    value = Math.round(+(value[0] + 'e' + (value[1] ? +value[1] + exp : exp)));

    // Shift back
    value = value.toString().split('e');
    return +(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp));
}

export const sleep = async (timer: number) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve(true);
        }, timer);
    });
};

export const validateMessages = {
    // eslint-disable-next-line no-template-curly-in-string
    required: '${label} is required!',
};

export const getTextAddress = (record: any, expand: boolean = true) => {
    if (typeof record?.location === 'object') {
        record.location = '';
    }
    return (record?.location || record?.address) && expand ? record?.location || record?.address : `${record?.district || ''}${record?.district ? ',' : ''} ${record?.province || ''}${record?.province ? ',' : ''} ${record?.country || ''}`;
};

export function dateFormat(date: any) {
    return moment(date).format('HH:mm:ss DD/MM/YYYY');
}

export function getImageUrl(record: any, param: string, backupImage: string) {
    if (record?.state?.toLowerCase() === 'completed') {
        return record?.state?.toLowerCase() === 'completed' && record[param] ? record[param]?.thumbnail || record[param]?.normal || record[param]?.origin : backupImage;
    }
    return record[param]?.origin || backupImage || '';
}

export function validatePassword() {
    return {
        validator: async (rule: any, value: any, callback: any) => {
            if (value) {
                const password = value;
                const passwordChecker = () => {
                    return {
                        number: !password || !password.replace(/[^0-9]/g, '')?.toString() || password === password.replace(/[^0-9]/g, '')?.toString(),
                        mixed: !/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password),
                        case: password.toUpperCase() === password || password.toLowerCase() === password,
                        minimum: password.length < 8,
                    };
                };
                if (passwordChecker().minimum) {
                    throw new Error('Password minimum 8 characters, at least one uppercase letter, one lowercase letter and one number');
                }
                if (passwordChecker().number) {
                    throw new Error('Password minimum 8 characters, at least one uppercase letter, one lowercase letter and one number');
                }
                if (passwordChecker().mixed) {
                    throw new Error('Password minimum 8 characters, at least one uppercase letter, one lowercase letter and one number');
                }
                if (passwordChecker().case) {
                    throw new Error('Password minimum 8 characters, at least one uppercase letter, one lowercase letter and one number');
                }
            }
        },
    };
}

export const getCurrentUser = async () => {
    try {
        const session = await fetchAuthSession();
        const tokens = session.tokens;

        if (!tokens) {
            return null;
        }

        const accessToken = tokens.accessToken;
        const idToken = tokens.idToken;

        let result: any = {
            attributes: idToken?.payload,
            AuthenticationResult: {
                AccessToken: accessToken?.toString(),
                IdToken: idToken?.toString(),
                RefreshToken: session.credentials ? 'refresh-token' : undefined,
                ExpiresIn: accessToken?.payload?.exp,
            },
            groups: (accessToken?.payload['cognito:groups'] as string[]) || [],
            permissions: [] as string[],
        };

        if (result && result?.groups?.includes('Administrators')) {
            // @ts-ignore
            window.user = result;
            // @ts-ignore
            const { data, status } = await getAdminProfile().catch((reason) => {
                return { data: null, status: 500 };
            });
            if (status === 200) {
                const adminData = data?.data || {};
                result = { ...result, ...adminData };

                // Fetch role permissions if user has a role
                if (adminData.role_id) {
                    try {
                        const roleResponse = await getRole(adminData.role_id);
                        if (roleResponse.status === 200 && roleResponse.data?.data) {
                            result.permissions = roleResponse.data.data.permissions || [];
                            result.role_name = roleResponse.data.data.name;
                        }
                    } catch (e) {
                        console.error('Failed to fetch role permissions:', e);
                    }
                }
            }
            return result;
        }
        return null;
    } catch (error) {
        return null;
    }
};

export const postLogin = async (user: any) => {
    try {
        const result = await signIn({
            username: user?.email?.toString().toLocaleLowerCase(),
            password: user.password,
        });

        if (result.isSignedIn) {
            const session = await fetchAuthSession();
            const tokens = session.tokens;

            if (!tokens) {
                return {
                    data: { message: 'Failed to get tokens' },
                    status: 400,
                };
            }

            const accessToken = tokens.accessToken;
            const idToken = tokens.idToken;

            const resultData: any = {
                data: {
                    attributes: idToken?.payload,
                    AuthenticationResult: {
                        AccessToken: accessToken?.toString(),
                        IdToken: idToken?.toString(),
                        RefreshToken: session.credentials ? 'refresh-token' : undefined,
                        ExpiresIn: accessToken?.payload?.exp,
                    },
                    groups: (accessToken?.payload['cognito:groups'] as string[]) || [],
                },
                status: 200,
            };
            return resultData;
        }

        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            return { status: 201, data: result };
        }

        return {
            data: { message: 'Sign in incomplete' },
            status: 400,
        };
    } catch (error: any) {
        return {
            data: {
                message: error.message || 'Sign in failed',
            },
            status: 400,
        };
    }
};

export const postSentOtp = async (user: any) => {
    try {
        const result = await resetPassword({ username: user.email });
        return result;
    } catch (error: any) {
        return {
            data: {
                message: error.message || 'Failed to send OTP',
            },
            status: 400,
        };
    }
};

export const postChangePassword = async (user: any, session: any) => {
    try {
        const result: any = await confirmSignIn({
            challengeResponse: user.password,
        });
        return result;
    } catch (error: any) {
        return {
            data: {
                message: error?.message || 'Failed to change password',
            },
            status: 400,
        };
    }
};

export const postResetPassword = async (user: any, session: any) => {
    try {
        const result: any = await confirmResetPassword({
            username: session.email,
            confirmationCode: user.code,
            newPassword: user.password,
        });
        return result;
    } catch (error: any) {
        return {
            data: {
                message: error?.message || 'Failed to reset password',
            },
            status: 400,
        };
    }
};

export const postUploadText = async (file: any, folder: string = 'temp') => {
    const result = await coreService.get('/upload-url', {
        params: {
            folder: folder,
            extension: 'json',
        },
    });

    if (result.status === 200 && result?.data?.data) {
        return axios
            .put(result.data.data, file, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            .then((value) => {
                return {
                    status: value.status,
                    data: result.data.data.toString().split('?')[0],
                };
            });
    }
};

export const uploadKML = async (file: any) => {
    const formData = new FormData();
    formData.append('', file);
    return coreService.post('/upload', formData, {
        headers: {
            'Content-Type': file.type,
        },
    });
};

export const saveImport = (trailId: string, filename: string) => appService.post(`/trail-map/${trailId}/import-history`, { name: filename });

export const getAppVersion = () => {
    return appService.get(`/app-version`);
};

export const postAppVersion = ({ version, message, force_update }: { version: string; message: string; force_update: boolean }) => {
    return appService.post(`/app-version`, {
        version,
        message,
        force_update,
    });
};

export const postUpload = async (file: any, direct: boolean = false, folder: string = 'temp') => {
    if (direct) {
        const formdata = new FormData();
        formdata.append('', file);
        return coreService
            .post('/upload', formdata, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            .then((result) => {
                if (result.status === 200) {
                    return {
                        status: 200,
                        data: result?.data?.data[0] || '',
                    };
                }
                return result;
            });
    } else {
        const fs = await new Promise((resolve) => {
            let reader = new FileReader();
            reader.onload = (e: any) => {
                resolve(e.target.result);
            };
            reader.readAsArrayBuffer(file);
        });
        const result = await coreService.get('/upload-url', {
            params: {
                folder: folder,
                extension: file.name.toString().split('.').pop(),
            },
        });

        if (result.status === 200 && result?.data?.data) {
            return axios
                .put(result.data.data, fs, {
                    headers: {
                        'Content-Type': file.type,
                    },
                })
                .then((value) => {
                    return {
                        status: value.status,
                        data: result.data.data.toString().split('?')[0],
                    };
                });
        }
    }
};

const getUploadUrls = async (files: Array<File>) => {
    const result = await coreService.post(
        '/upload-url',
        files.map((file) => {
            return {
                name: file.name,
                type: file.type,
            };
        })
    );
    if (result.status === 200) {
        return result?.data?.data || [];
    }
    return [];
};

const getFileBuffer = async (file: File) => {
    return await new Promise((resolve) => {
        let reader = new FileReader();
        reader.onload = (e: any) => {
            resolve(e.target.result);
        };
        reader.readAsArrayBuffer(file);
    });
};

const putS3File = async (file: File, uri: string, fileUrl: string) => {
    const fs = await getFileBuffer(file);
    if (fs) {
        return axios
            .put(uri, fs, {
                headers: {
                    'Content-Type': file.type,
                },
            })
            .then((value) => {
                return {
                    status: value.status,
                    data: fileUrl,
                };
            })
            .catch((reason) => {
                return {
                    status: 500,
                    data: null,
                };
            });
    }
    return null;
};

export const postUploads = async (files: Array<File>) => {
    const uploadUrls = await getUploadUrls(files);
    const result = [];
    if (uploadUrls.length === files.length) {
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const fileUrl = uploadUrls[i]?.url;
            const uploadUrl = uploadUrls[i]?.uploadUrl;
            const promise = putS3File(file, uploadUrl, fileUrl);
            result.push(promise);
        }
    }
    return result;
};

export const getLocation = (country: string) => {
    return axios.get(`${Environment.locationUrl}/country/${country}.json`);
};

export const truncate = (str: string, n: number) => {
    return str?.length > n ? str.slice(0, n - 1) + '...' : str || '';
};

export const getJsonContent = (uri: string) => {
    return axios.get(uri);
};

/**
 * Generates a GPX file content from an array of coordinates
 * @param {Array<Array<number>>} coordinates - Array of [longitude, latitude] coordinates
 * @param {string} title - Title of the track
 * @param {number} elevationGain - Total elevation gain in meters
 * @param {number} elevationLoss - Total elevation loss in meters
 * @param {Array<any>} elevationPoints - Array of elevation data points
 * @returns {string} - GPX file content as string
 */
export const generateGpx = (coordinates: Array<Array<number>>, title: string = 'Track', elevationGain: number = 0, elevationLoss: number = 0, elevationPoints: Array<any> = []) => {
    if (!coordinates || coordinates.length === 0) {
        return '';
    }

    // Use elevation data from elevation points if available
    const hasElevationData = elevationPoints && elevationPoints.length > 0;

    const trackpoints = coordinates
        .map((coord, index) => {
            // Try to find matching elevation data
            let elevation = 0;
            if (hasElevationData) {
                const matchingPoint = elevationPoints.find((point) => point.coordinates && point.coordinates[0] === coord[0] && point.coordinates[1] === coord[1]);
                elevation = matchingPoint ? matchingPoint.elevation : 0;
            } else {
                elevation = coord[2] || 0;
            }

            // GPX format uses [latitude, longitude] order
            return `<trkpt lat="${coord[1]}" lon="${coord[0]}">
                <ele>${elevation}</ele>
            </trkpt>`;
        })
        .join('\n');

    const gpxContent = `<?xml version="1.0" encoding="UTF-8"?>
        <gpx version="1.1" creator="EXO CMS" xmlns="http://www.topografix.com/GPX/1/1">
            <metadata>
                <name>${title}</name>
                <time>${new Date().toISOString()}</time>
                <extensions>
                    <elevation-gain>${elevationGain}</elevation-gain>
                    <elevation-loss>${elevationLoss}</elevation-loss>
                </extensions>
            </metadata>
            <trk>
                <name>${title}</name>
                <extensions>
                    <elevation-gain>${elevationGain}</elevation-gain>
                    <elevation-loss>${elevationLoss}</elevation-loss>
                </extensions>
                <trkseg>
                    ${trackpoints}
                </trkseg>
            </trk>
        </gpx>`;

    return gpxContent;
};

/**
 * Creates a download link for GPX data
 * @param {string} gpxContent - The GPX content as string
 * @param {string} filename - The name for the downloaded file
 */
export const downloadGpxFile = (gpxContent: string, filename: string = 'track.gpx') => {
    if (!gpxContent || gpxContent === '') return;

    const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
};

export const geoJsonConverter = (values: any, type: 'in' | 'out') => {
    if (type === 'out') {
        return {
            coordinates: values.coordinates || [],
            planned_elevation: values.planned_elevation || [],
            dem_elevation: values.dem_elevation || [],
            guides: values.guides || [],
            splits: values?.splits?.length >= 0 ? values?.splits : undefined,
            timestamps: values?.timestamps || [],
        };
    } else {
        return {
            coordinates: values?.coordinates || [],
            planned_elevation: values?.planned_elevation || [],
            dem_elevation: values?.dem_elevation || [],
            guides: values.guides || [],
            splits: values?.splits?.length >= 0 ? values?.splits : undefined,
            timestamps: values?.timestamps || [],
        };
    }
};

export const navDetail = (id: string, navigate: any, type: string) => {
    // let route = `/user/customers/update/${id}`;
    // if (type === 'admin') {
    //     route = `/user/admins/update/${id}`;
    // }
    // if (type === 'attribute') {
    //     route = `/trail/attributes/update/${id}`;
    // }
    // if (type === 'trail') {
    //     route = `/trail/trails/update/${id}`;
    // }
    // navigate(route);
};

// Admin APIs
export const getAdminProfile = () => coreService.get('/admin/profile');
export const getAdmin = (id: string) => coreService.get(`/admin/${id}`);
export const listAdmin = (params: any) => coreService.get(`/admin`, { params: params });
export const deleteAdmin = (id: string) => coreService.delete(`/admin/${id}`);
export const putAdmin = (id: string, data: any) => coreService.put(`/admin/${id}`, data);
export const postAdmin = (data: any) => coreService.post(`/admin`, data);

// User APIs
export const getUser = (id: string) => coreService.get(`/users/${id}`);
export const listUser = (params: any) => coreService.get(`/users`, { params: params });
export const listUserByIds = (ids: any) => coreService.get(`/users/list-by-ids`, { params: { ids: (ids || []).join(',') } });
export const listUserByActivities = (params: any) => coreService.get(`/users/list-by-activities`, { params: params });
export const deleteUser = (id: string) => coreService.delete(`/users/${id}`);
export const putUser = (id: string, data: any) => coreService.put(`/users/${id}`, data);
export const postUser = (data: any) => coreService.post(`/users`, data);

// Attribute APIs
export const getAttribute = (id: string) => appService.get(`/attribute/${id}`);
export const listAttribute = (params: any) => appService.get(`/attribute`, { params: params });
export const deleteAttribute = (id: string) => appService.delete(`/attribute/${id}`);
export const putAttribute = (id: string, data: any) => appService.put(`/attribute/${id}`, data);
export const postAttribute = (data: any) => appService.post(`/attribute`, data);

// Trail APIs
export const getTrail = (id: string) => appService.get(`/trail/${id}`);
export const getTrailCreatorById = (id: string) => appService.get(``);
export const getTrailAttribute = (id: string) => appService.get(`/trail-attribute/${id}`);
export const getTrailOwner = (id: string) => appService.get(`/trail-owner/${id}`);
export const listTrail = (params: any) => appService.get(`/trail`, { params: params });
export const listRegions = (params?: any) => appService.get(`/region`, { params: params });
export const listTrailByIds = (ids: any) => appService.get(`/trail/list-by-ids`, { params: { ids: (ids || []).join(',') } });
export const deleteTrail = (id: string) => appService.delete(`/trail/${id}`);
export const putTrail = (id: string, data: any) => appService.put(`/trail/${id}`, data);
export const postTrail = (data: any) => appService.post(`/trail`, data);
export const putTrailAttribute = (id: string, data: any) => appService.put(`/trail-attribute/${id}`, { ...data, trail_id: id });
export const postTrailOwner = (id: string, data: any) => appService.post(`/trail-owner`, { trail_id: id });
export const putTrailTranslate = (id: string, translate_to: string, provider: string = 'chatgpt') => appService.put(`/trail/${id}/translate`, { translate_to, provider });

// Trail Map APIs
export const getImportedList = (trailId: string) => appService.get(`/trail-map/${trailId}/import-history`);
export const getTrailMap = (id: string) => appService.get(`/trail-map/${id}`);
export const getUpdateTrailHistory = (id: string) => appService.get(`/trail-map/${id}/histories`);
export const restoreHistory = (trail_id: string, history_id: string) => appService.put(`/trail-map/${trail_id}/restore`, { history_id });
export const putTrailMap = (id: string, data: any) => appService.put(`/trail-map/${id}`, { ...data });
export const putOverrideTrailMapAsActivity = (trail_id: string, activity_id: string) => appService.put(`/trail-map/${trail_id}`, { activity_id });
export const getAddress = (location: any) => {
    const address = window.encodeURIComponent(`${location?.district}, ${location?.province}, ${location?.country}`);
    return axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${address}.json?country${location.country}&access_token=${Environment.mapboxKey}`);
};
export const putOverride = (trail_id: string, activity_id: string) => appService.put(`/mobile/trail-map/${trail_id}/override`, { activity_id });
export const putOverrideElevation = (trail_id: string, windowSize: number = 7) => appService.put(`/mobile/trail-map/${trail_id}/elevation?windowSize=${windowSize}`);

// Review APIs
export const getReview = (id: string) => appService.get(`/reviews/${id}`);
export const listReview = (params: any) => appService.get(`/reviews`, { params: params });
export const deleteReview = (id: string) => appService.delete(`/reviews/${id}`);
export const putReview = (id: string, data: any) => appService.put(`/reviews/${id}`, data);
export const postReview = (data: any) => appService.post(`/reviews`, data);

// Photo APIs
export const getPhoto = (id: string) => appService.get(`/photos/${id}`);
export const listPhoto = (params: any) => appService.get(`/photos`, { params: params });
export const deletePhoto = (id: string) => appService.delete(`/photos/${id}`);
export const putPhoto = (id: string, data: any) => appService.put(`/photos/${id}`, data);
export const postPhoto = (data: any) => appService.post(`/photos`, data);

// Activity APIs
export const getActivity = (id: string) => appService.get(`/activity/${id}`);
export const listActivity = (params: any) => appService.get(`/activity`, { params: { ...params, status: 'Active,Inactive,Verified' } });
export const deleteActivity = (id: string) => appService.delete(`/activity/${id}`);
export const putActivity = (id: string, data: any) => appService.put(`/activity/${id}`, data);
export const postActivity = (data: any) => appService.post(`/activity`, data);

// Advertising APIs
export const getSettingAds = (id: string) => appService.get(`/advertising/${id}`);
export const listSettingAds = () => appService.get(`/advertising`);
export const putSettingAds = (id: string, data: any) => appService.put(`/advertising/${id}`, data);
export const postSettingAds = (data: any) => appService.post(`/advertising`, data);

// Crawl APIs
export const getCrawl = (id: string) => appService.get(`/crawl/${id}`);
export const listCrawl = (params: any) => appService.get(`/crawl`, { params: params });
export const listCrawlRegions = (params: any) =>
    appService.get(`/crawl/source-regions`, { params: {} }).then((value) => {
        value.data.data.items = value.data.data.map((x: string) => {
            return {
                id: x,
                name: x,
            };
        });
        return value;
    });
export const listCrawlSources = (params: any) =>
    appService.get(`/crawl/sources`, { params: {} }).then((value) => {
        value.data.data.items = value.data.data;
        console.log(value);
        return value;
    });
export const deleteCrawl = (id: string) => appService.delete(`/crawl/${id}`);
export const putCrawl = (id: string, data: any) => appService.put(`/crawl/${id}`, data);
export const putCrawlReject = (id: string) => appService.put(`/crawl/${id}/reject`, {});
export const putCrawlCancel = (id: string) => appService.put(`/crawl/${id}/cancel`, {});
export const putCrawlAccept = (id: string) => appService.put(`/crawl/${id}/accept`, {});
export const putCrawlRestore = (id: string) => appService.put(`/crawl/${id}/restore`, {});
export const putCrawlMigrate = (id: string) => appService.put(`/crawl/${id}/migrate`, {});
export const putCrawlRollback = (id: string) => appService.put(`/crawl/${id}/rollback`, {});
export const postCrawl = (data: any) => appService.post(`/crawl`, data);

// Challenge APIs
export const listChallenge = (params: any) => socialService.get(`/challenge/list-by-ids`, { params: params });
export const listEventChallenges = (eventId: string, params: any) => socialService.get(`/event/${eventId}/challenge`, { params });
export const getChallenge = (id: string) => socialService.get(`/challenge/${id}`);
export const getChallengeLiveTracking = (id: string) => socialService.get(`/challenge/${id}/live-tracking`);

// Configuration Settings APIs
export const getConfigSetting = () => appService.get(`/configuration`);
export const postConfigSetting = (data: any) => appService.post(`/configuration`, data);
export const putConfigSetting = (id: string, data: any) => appService.put(`/configuration/${id}`, data);
export const deleteConfigSetting = (id: string) => appService.delete(`/configuration/${id}`);
export const createGpsSettings = async (data: any) => {
    // Check if GPS settings already exist
    const response = await getConfigSetting();
    const gpsSettings = response?.data?.data?.items.find((item: any) => item.key === 'gps-setting');

    if (gpsSettings) {
        // Update existing settings
        return putConfigSetting(gpsSettings.id, {
            ...gpsSettings,
            value: JSON.stringify(data),
        });
    } else {
        // Create new settings
        return postConfigSetting({
            key: 'gps-setting',
            value: JSON.stringify(data),
            note: 'GPS filter conditions for different activities',
        });
    }
};

// Notify Schedule APIs
export const getConfigNotify = () => socialService.get(`/notify-schedule`);
export const deleteConfigNotify = (id: string) => socialService.delete(`/notify-schedule/${id}`);
export const putConfigNotify = (id: string, data: any) => socialService.put(`/notify-schedule/${id}`, data);
export const postConfigNotify = (data: any) => socialService.post(`/notify-schedule`, data);

export const getUserStats = (userId: string, params?: any) => appService.get(`/mobile/v2/stats?${new URLSearchParams({ user_id: userId, ...params }).toString()}`);

// Membership APIs
export const getMembership = (id: string) => coreService.get(`/membership/${id}`);
export const listMembership = (params: any) => coreService.get(`/membership`, { params: params });
export const deleteMembership = (id: string) => coreService.delete(`/membership/${id}`);
export const putMembership = (id: string, data: any) => coreService.put(`/membership/${id}`, data);
export const postMembership = (data: any) => coreService.post(`/membership`, data);

// Ticketing APIs
export const getTicket = (id: string) => socialService.get(`/ticket/${id}`);
export const listTicket = (params: any) => socialService.get(`/ticket`, { params: params });
export const listMyTicket = (params: any) => socialService.get(`/ticket/my-tickets`, { params: params });
export const putTicket = (id: string, data: any) => socialService.put(`/ticket/${id}`, data);
export const postTicket = (data: any) => socialService.post(`/ticket`, data);
export const postTicketMessage = (id: string, data: any) => socialService.post(`/ticket/${id}/message`, data);

// ExoSeed Campaign APIs
export const listExoseedCampaign = (params: any) => exoseedService.get(`/exoseed/campaign`, { params: params });
export const getExoseedCampaign = (id: string) => exoseedService.get(`/exoseed/campaign/${id}`);
export const postExoseedCampaign = (data: any) => exoseedService.post(`/exoseed/campaign`, data);
export const putExoseedCampaign = (id: string, data: any) => exoseedService.put(`/exoseed/campaign/${id}`, data);
export const deleteExoseedCampaign = (id: string) => exoseedService.delete(`/exoseed/campaign/${id}`);
export const getExoseedCampaignStats = (id: string) => exoseedService.get(`/exoseed/campaign/${id}/stats`);
export const getExoseedCampaignParticipation = (id: string) => exoseedService.get(`/exoseed/campaign/${id}/participation`);

// ExoSeed Quest APIs
export const postExoseedQuest = (campaignId: string, data: any) => exoseedService.post(`/exoseed/campaign/${campaignId}/quest`, data);
export const putExoseedQuest = (campaignId: string, questId: string, data: any) => exoseedService.put(`/exoseed/campaign/${campaignId}/quest/${questId}`, data);
export const deleteExoseedQuest = (campaignId: string, questId: string) => exoseedService.delete(`/exoseed/campaign/${campaignId}/quest/${questId}`);
export const getExoseedQuest = (campaignId: string, questId: string) => exoseedService.get(`/exoseed/campaign/${campaignId}/quest/${questId}`);
export const getExoseedQuestStats = (campaignId: string, questId: string) => exoseedService.get(`/exoseed/campaign/${campaignId}/quest/${questId}/stats`);

// ExoSeed Progress APIs
export const listExoseedProgress = (params: any) => exoseedService.get(`/exoseed/progress`, { params: params });
export const getExoseedUserProgress = (userId: string) => exoseedService.get(`/exoseed/user/${userId}/progress`);
export const getExoseedUserCampaignProgress = (userId: string, campaignId: string) => exoseedService.get(`/exoseed/user/${userId}/campaign/${campaignId}`);
export const getExoseedUserQuestProgress = (userId: string, questId: string) => exoseedService.get(`/exoseed/user/${userId}/quest/${questId}`);
export const getExoseedUserHistory = (userId: string, params?: any) => exoseedService.get(`/exoseed/user/${userId}/history`, { params: params });
export const getExoseedUserHistoryLatest = (userId: string) => exoseedService.get(`/exoseed/user/${userId}/history/latest`);

// Event APIs
export const listEvent = (params: any) => exoseedService.get(`/event`, { params: params });
export const getEvent = (id: string) => exoseedService.get(`/event/${id}`);
export const postEvent = (data: any) => exoseedService.post(`/event`, data);
export const putEvent = (id: string, data: any) => exoseedService.put(`/event/${id}`, data);
export const deleteEvent = (id: string) => exoseedService.delete(`/event/${id}`);
export const getEventStats = (id: string) => exoseedService.get(`/event/${id}/stats`);
export const addTrailToEvent = (eventId: string, trailId: string) => exoseedService.post(`/event/${eventId}/trail`, { trail_id: trailId });
export const removeTrailFromEvent = (eventId: string, trailId: string) => exoseedService.delete(`/event/${eventId}/trail/${trailId}`);
export const addChallengeToEvent = (eventId: string, challengeId: string) => exoseedService.post(`/event/${eventId}/challenge`, { challenge_id: challengeId });
export const removeChallengeFromEvent = (eventId: string, challengeId: string) => exoseedService.delete(`/event/${eventId}/challenge/${challengeId}`);

export const getChallengeTrackings = (challengeIds: string, params?: any) => socialService.get(`/challenge/trackings`, { params: { challenge_ids: challengeIds, ...params } });

// Role and Permission APIs
export const listRole = (params: any) => coreService.get(`/roles`, { params: params });
export const getRole = (id: string) => coreService.get(`/roles/${id}`);
export const postRole = (data: any) => coreService.post(`/roles`, data);
export const putRole = (id: string, data: any) => coreService.put(`/roles/${id}`, data);
export const deleteRole = (id: string) => coreService.delete(`/roles/${id}`);

// Group permissions by category for easier display
export const getGroupedPermissions = () => {
    const groups: { [key: string]: typeof CMS_PERMISSIONS } = {};
    CMS_PERMISSIONS.forEach((permission) => {
        if (!groups[permission.group]) {
            groups[permission.group] = [];
        }
        groups[permission.group].push(permission);
    });
    return groups;
};

// ExoSeed Badge APIs
export const listBadge = (params: any) => exoseedService.get(`/badge`, { params: params });
export const getBadge = (id: string) => exoseedService.get(`/badge/${id}`);
export const postBadge = (data: any) => exoseedService.post(`/badge`, data);
export const putBadge = (id: string, data: any) => exoseedService.put(`/badge/${id}`, data);
export const deleteBadge = (id: string) => exoseedService.delete(`/badge/${id}`);

// ExoSeed Reward APIs
export const listReward = (params: any) => exoseedService.get(`/reward`, { params: params });
export const getReward = (id: string) => exoseedService.get(`/reward/${id}`);
export const postReward = (data: any) => exoseedService.post(`/reward`, data);
export const putReward = (id: string, data: any) => exoseedService.put(`/reward/${id}`, data);
export const deleteReward = (id: string) => exoseedService.delete(`/reward/${id}`);
export const postVariantProducts = (rewardId: string, variantId: string, products: any[]) => exoseedService.post(`/reward/${rewardId}/variant/${variantId}/product`, { products });
export const deleteVariantProduct = (rewardId: string, variantId: string, productId: string) => exoseedService.delete(`/reward/${rewardId}/variant/${variantId}/product/${productId}`);

// ExoSeed Stamp APIs
export const listStamp = (params: any) => exoseedService.get(`/stamp`, { params: params });
export const getStamp = (id: string) => exoseedService.get(`/stamp/${id}`);
export const postStamp = (data: any) => exoseedService.post(`/stamp`, data);
export const putStamp = (id: string, data: any) => exoseedService.put(`/stamp/${id}`, data);
export const deleteStamp = (id: string) => exoseedService.delete(`/stamp/${id}`);

// ExoSeed Offer APIs
export const listOffer = (params: any) => exoseedService.get(`/offer`, { params: params });
export const getOffer = (id: string) => exoseedService.get(`/offer/${id}`);
export const postOffer = (data: any) => exoseedService.post(`/offer`, data);
export const putOffer = (id: string, data: any) => exoseedService.put(`/offer/${id}`, data);
export const deleteOffer = (id: string) => exoseedService.delete(`/offer/${id}`);
export const getOfferInventory = (params: any) => exoseedService.get(`/inventory`, { params: params });
export const putInventory = (id: string, data: any) => exoseedService.put(`/inventory/${id}`, data);
